import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  X, Settings, BarChart3, Activity, MessageSquare, Users, 
  Eye, Clock, Signal, AlertCircle, CheckCircle2, Wifi,
  WifiOff, Send, Trash2, Ban, RefreshCw, Play, Pause,
  Volume2, VolumeX, Maximize, ChevronDown, Loader2,
  Heart, ThumbsUp, Copy, ExternalLink, Shield, Calendar
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const StreamControlPanel = ({ event, authToken, onClose }) => {
  const [activeTab, setActiveTab] = useState('settings');
  const [streamStatus, setStreamStatus] = useState('offline');
  const [cfStatus, setCfStatus] = useState('disconnected');
  const [viewerCount, setViewerCount] = useState(0);
  const [streamDuration, setStreamDuration] = useState(0);
  const [healthLogs, setHealthLogs] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [playbackUrl, setPlaybackUrl] = useState('');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [streamOptions, setStreamOptions] = useState({
    dvr: true, low_latency: true, recording: true
  });
  const [togglingOption, setTogglingOption] = useState(null);
  const [streamHealth, setStreamHealth] = useState({
    bitrate: 0, fps: 0, resolution: '0x0',
    status: 'No data', quality: 'unknown'
  });
  
  const chatEndRef = useRef(null);
  const refreshInterval = useRef(null);
  const healthLogsRef = useRef([]);
  
  const addHealthLog = useCallback((type, message) => {
    const log = { id: Date.now(), type, message, timestamp: new Date().toISOString() };
    healthLogsRef.current = [...healthLogsRef.current.slice(-49), log];
    setHealthLogs([...healthLogsRef.current]);
  }, []);

  const fetchStreamData = useCallback(async () => {
    try {
      if (event?.id) {
        const res = await axios.get(`${API}/api/livestream/admin/event/${event.id}/stream-status`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        
        if (res.data) {
          const isLive = res.data.is_live;
          setStreamStatus(isLive ? 'live' : 'offline');
          setCfStatus(res.data.cf_status || 'disconnected');
          setViewerCount(res.data.viewer_count || 0);
          
          if (res.data.playback_url) {
            setPlaybackUrl(res.data.playback_url);
          }
          
          if (res.data.stream_options) {
            setStreamOptions(res.data.stream_options);
          }
          
          if (res.data.health) {
            setStreamHealth(res.data.health);
          }
          
          // Add health log entries
          if (res.data.cf_status === 'connected') {
            addHealthLog('info', `Stream active - ${res.data.health?.bitrate || 0} kbps @ ${res.data.health?.resolution || '0x0'}`);
          } else if (res.data.cf_status === 'disconnected' && res.data.live_input_id) {
            addHealthLog('warning', 'Encoder disconnected - waiting for stream input');
          }
        }
      }
    } catch (err) {
      console.log('Error fetching stream data:', err);
      addHealthLog('error', 'Failed to fetch stream status');
    } finally {
      setLoading(false);
    }
  }, [event?.id, authToken, addHealthLog]);

  const fetchChatMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/livestream/admin/event/${event.id}/chat`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.data?.messages) {
        setChatMessages(res.data.messages);
      }
    } catch (err) {
      if (chatMessages.length === 0) {
        setChatMessages([
          { id: '1', user: 'System', message: 'Chat will appear here during the stream', timestamp: new Date().toISOString(), isSystem: true }
        ]);
      }
    }
  }, [event?.id, authToken]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/livestream/admin/event/${event.id}/control-panel-analytics`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.data) {
        setAnalyticsData(res.data);
        if (res.data.stream_duration > 0) {
          setStreamDuration(res.data.stream_duration);
        }
      }
    } catch (err) {
      console.log('Error fetching analytics:', err);
    }
  }, [event?.id, authToken]);

  useEffect(() => {
    fetchStreamData();
    fetchChatMessages();
    fetchAnalytics();
    
    refreshInterval.current = setInterval(() => {
      fetchStreamData();
      fetchChatMessages();
    }, 8000);
    
    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    };
  }, [fetchStreamData, fetchChatMessages, fetchAnalytics]);
  
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const toggleStreamOption = async (option) => {
    setTogglingOption(option);
    const newValue = !streamOptions[option];
    
    try {
      await axios.put(`${API}/api/livestream/admin/event/${event.id}/stream-options`, 
        { [option]: newValue },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setStreamOptions(prev => ({ ...prev, [option]: newValue }));
      addHealthLog('info', `${option.replace('_', ' ')} ${newValue ? 'enabled' : 'disabled'}`);
    } catch (err) {
      addHealthLog('error', `Failed to update ${option}`);
    } finally {
      setTogglingOption(null);
    }
  };
  
  const sendChatMessage = async () => {
    if (!newMessage.trim()) return;
    const msg = newMessage;
    setNewMessage('');
    
    setChatMessages(prev => [...prev, {
      id: Date.now().toString(), user: 'Admin', message: msg,
      timestamp: new Date().toISOString(), isAdmin: true
    }]);

    try {
      await axios.post(`${API}/api/livestream/admin/event/${event.id}/chat`,
        { message: msg, is_admin: true },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
    } catch (err) { /* message already shown locally */ }
  };
  
  const deleteMessage = async (messageId) => {
    setChatMessages(prev => prev.filter(m => m.id !== messageId));
    try {
      await axios.delete(`${API}/api/livestream/admin/event/${event.id}/chat/${messageId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
    } catch (err) { /* already removed from UI */ }
  };
  
  const formatDuration = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'live': return 'bg-red-500';
      case 'connecting': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };
  
  const getHealthColor = (quality) => {
    switch (quality) {
      case 'excellent': return 'text-green-400';
      case 'good': return 'text-green-400';
      case 'fair': return 'text-yellow-400';
      case 'poor': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  // Simple bar chart component for viewer timeline
  const ViewerChart = ({ data }) => {
    if (!data || data.length === 0) {
      return (
        <div className="h-48 flex items-center justify-center text-gray-500">
          <div className="text-center">
            <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No viewer data available yet</p>
          </div>
        </div>
      );
    }
    
    const maxViewers = Math.max(...data.map(d => d.viewers), 1);
    
    return (
      <div className="h-48 flex items-end gap-1 px-2" data-testid="viewer-chart">
        {data.map((point, i) => {
          const height = (point.viewers / maxViewers) * 100;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative min-w-0">
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10 transition-opacity">
                {point.viewers} viewers @ {point.time}
              </div>
              <div 
                className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all duration-300 min-h-[2px]"
                style={{ height: `${Math.max(height, 2)}%` }}
              />
              {i % Math.max(1, Math.floor(data.length / 6)) === 0 && (
                <span className="text-[10px] text-gray-500 truncate w-full text-center">{point.time}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };
  
  return (
    <div className="fixed inset-0 z-50 bg-[#0f0f0f]" data-testid="stream-control-panel">
      {/* Header */}
      <div className="h-14 bg-[#202020] border-b border-gray-800 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3">
            <img src="/ltv-live-logo.png" alt="LTV Live" className="h-8" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="font-bold text-white">Control Panel</span>
          </div>
          <Badge data-testid="stream-status-badge" className={`${getStatusColor(streamStatus)} text-white`}>
            {streamStatus === 'live' ? (
              <><span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse" />LIVE</>
            ) : (
              streamStatus.toUpperCase()
            )}
          </Badge>
          {(streamStatus === 'live' || streamDuration > 0) && (
            <span className="text-gray-400 text-sm">{formatDuration(streamDuration)}</span>
          )}
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-gray-400">
            <Eye className="h-4 w-4" />
            <span className="font-medium" data-testid="viewer-count">{viewerCount}</span>
            <span className="text-sm">viewers</span>
          </div>
          <Button variant="ghost" onClick={onClose} className="text-gray-400 hover:text-white" data-testid="close-control-panel">
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>
      
      <div className="flex h-[calc(100vh-56px)]">
        {/* Left Sidebar */}
        <div className="w-14 bg-[#202020] border-r border-gray-800 flex flex-col items-center py-4 gap-4">
          <button className="p-2 text-white bg-white/10 rounded-lg" title="Go Live"><Signal className="h-5 w-5" /></button>
          <button className="p-2 text-gray-400 hover:text-white rounded-lg" title="Recordings"><Play className="h-5 w-5" /></button>
          <button className="p-2 text-gray-400 hover:text-white rounded-lg" title="Schedule"><Calendar className="h-5 w-5" /></button>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Stream Preview */}
          <div className="relative bg-black h-[360px] min-h-[300px]" data-testid="stream-preview">
            {playbackUrl ? (
              <iframe
                src={playbackUrl}
                className="w-full h-full"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Stream Preview"
              />
            ) : event?.cloudflare_stream_id ? (
              <iframe
                src={`https://iframe.videodelivery.net/${event.cloudflare_stream_id}?muted=true&autoplay=true`}
                className="w-full h-full"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Stream Preview"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 bg-[#282828]">
                <WifiOff className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-xl font-medium text-white">Connect your encoder to go live</p>
                <p className="text-sm mt-2 text-gray-400">Copy and paste the stream key into your encoder (OBS, Streamlabs, etc.)</p>
              </div>
            )}
            
            {/* Stream Status Overlay */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <Badge className="bg-[#282828]/80 backdrop-blur text-white px-3 py-1 border border-gray-700" data-testid="cf-status-badge">
                <span className={`w-2 h-2 rounded-full mr-2 ${
                  cfStatus === 'connected' ? 'bg-green-500 animate-pulse' : 
                  streamStatus === 'live' ? 'bg-green-500 animate-pulse' : 'bg-gray-500'
                }`} />
                {cfStatus === 'connected' ? 'LIVE' : streamStatus === 'live' ? 'LIVE' : 'Offline'}
              </Badge>
            </div>
            
            {/* Stream Key Dropdown */}
            <div className="absolute top-4 right-4 flex items-center gap-2">
              <Button variant="outline" size="sm" className="bg-[#282828]/80 backdrop-blur border-gray-700 text-white hover:bg-gray-700">
                Stream key: {event?.title?.substring(0, 15) || 'Default stream'}...
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white" onClick={fetchStreamData}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Status Bar */}
          <div className="bg-[#282828] border-y border-gray-800 px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <span className={`w-2 h-2 rounded-full ${cfStatus === 'connected' || streamStatus === 'live' ? 'bg-green-500' : 'bg-gray-500'}`} />
              <span className="text-gray-300">
                {cfStatus === 'connected' || streamStatus === 'live'
                  ? 'Stream is live and receiving data' 
                  : event?.cloudflare_live_input_id 
                    ? 'Live input ready - start sending video from your streaming software'
                    : 'No live input configured - create one from the event settings'}
              </span>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex-1 overflow-hidden flex flex-col bg-[#181818]">
            <div className="flex border-b border-gray-800">
              {['settings', 'analytics', 'health'].map(tab => (
                <button
                  key={tab}
                  onClick={() => { setActiveTab(tab); if (tab === 'analytics') fetchAnalytics(); }}
                  data-testid={`tab-${tab}`}
                  className={`px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab ? 'text-white border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {tab === 'settings' ? 'Stream settings' : tab === 'analytics' ? 'Analytics' : 'Stream health'}
                </button>
              ))}
            </div>
            
            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6 max-w-2xl" data-testid="settings-tab-content">
                  <h3 className="text-lg font-bold text-white">Stream Settings</h3>
                  
                  {/* Stream Credentials */}
                  <div className="space-y-4">
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                      <label className="text-sm text-gray-400 block mb-2">RTMPS URL (Server)</label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-sm text-orange-400 bg-black/50 p-3 rounded-lg font-mono break-all">
                          {event?.cloudflare_rtmps_url || 'rtmps://live.cloudflare.com:443/live/'}
                        </code>
                        <Button size="sm" variant="outline" data-testid="copy-rtmps-url"
                          onClick={() => navigator.clipboard.writeText(event?.cloudflare_rtmps_url || 'rtmps://live.cloudflare.com:443/live/')}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                      <label className="text-sm text-gray-400 block mb-2">Stream Key</label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-sm text-purple-400 bg-black/50 p-3 rounded-lg font-mono break-all">
                          {event?.cloudflare_stream_key || 'No stream key configured'}
                        </code>
                        <Button size="sm" variant="outline" data-testid="copy-stream-key"
                          onClick={() => navigator.clipboard.writeText(event?.cloudflare_stream_key || '')}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {event?.cloudflare_srt_url && (
                      <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                        <label className="text-sm text-gray-400 block mb-2">SRT URL</label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-sm text-cyan-400 bg-black/50 p-3 rounded-lg font-mono break-all">
                            {event.cloudflare_srt_url}
                          </code>
                          <Button size="sm" variant="outline"
                            onClick={() => navigator.clipboard.writeText(event.cloudflare_srt_url)}>
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Stream Options - Functional Toggles */}
                  <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="stream-options">
                    <h4 className="font-medium text-white mb-4">Stream Options</h4>
                    <div className="space-y-4">
                      {[
                        { key: 'dvr', label: 'DVR (Rewind)', desc: 'Allow viewers to rewind live content' },
                        { key: 'low_latency', label: 'Low Latency', desc: 'Reduce delay for interactive streams' },
                        { key: 'recording', label: 'Recording', desc: 'Automatically record this stream' }
                      ].map(opt => (
                        <div key={opt.key} className="flex items-center justify-between">
                          <div>
                            <p className="text-white">{opt.label}</p>
                            <p className="text-sm text-gray-400">{opt.desc}</p>
                          </div>
                          <button 
                            data-testid={`toggle-${opt.key}`}
                            onClick={() => toggleStreamOption(opt.key)}
                            disabled={togglingOption === opt.key}
                            className={`relative w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
                              streamOptions[opt.key] ? 'bg-green-600' : 'bg-gray-600'
                            } ${togglingOption === opt.key ? 'opacity-50' : ''}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                              streamOptions[opt.key] ? 'translate-x-6' : 'translate-x-0'
                            }`} />
                            {togglingOption === opt.key && (
                              <Loader2 className="absolute inset-0 m-auto h-3 w-3 animate-spin text-white" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Analytics Tab */}
              {activeTab === 'analytics' && (
                <div className="space-y-6" data-testid="analytics-tab-content">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white">Live Analytics</h3>
                    <Button size="sm" variant="outline" onClick={fetchAnalytics} className="border-gray-700" data-testid="refresh-analytics">
                      <RefreshCw className="h-4 w-4 mr-2" />Refresh
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="current-viewers-card">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <Eye className="h-4 w-4" /><span className="text-sm">Current Viewers</span>
                      </div>
                      <p className="text-3xl font-bold text-white">{analyticsData?.current_viewers ?? viewerCount}</p>
                    </div>
                    
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="peak-viewers-card">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <Users className="h-4 w-4" /><span className="text-sm">Peak Viewers</span>
                      </div>
                      <p className="text-3xl font-bold text-white">{analyticsData?.peak_viewers ?? 0}</p>
                    </div>
                    
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="stream-duration-card">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <Clock className="h-4 w-4" /><span className="text-sm">Stream Duration</span>
                      </div>
                      <p className="text-3xl font-bold text-white">{formatDuration(analyticsData?.stream_duration ?? streamDuration)}</p>
                    </div>
                    
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="chat-messages-card">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <MessageSquare className="h-4 w-4" /><span className="text-sm">Chat Messages</span>
                      </div>
                      <p className="text-3xl font-bold text-white">{analyticsData?.chat_messages ?? chatMessages.length}</p>
                    </div>
                  </div>

                  {/* Extra stats row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="unique-viewers-card">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <Users className="h-4 w-4" /><span className="text-sm">Unique Viewers</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{analyticsData?.unique_viewers ?? 0}</p>
                    </div>
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="tickets-sold-card">
                      <div className="flex items-center gap-2 text-gray-400 mb-2">
                        <Heart className="h-4 w-4" /><span className="text-sm">Tickets Sold</span>
                      </div>
                      <p className="text-2xl font-bold text-white">{analyticsData?.tickets_sold ?? 0}</p>
                      {analyticsData?.total_revenue > 0 && (
                        <p className="text-xs text-green-400 mt-1">Revenue: £{analyticsData.total_revenue.toFixed(2)}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Viewer Graph */}
                  <div className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-testid="viewer-timeline-section">
                    <h4 className="font-medium text-white mb-4">Viewer Count Over Time</h4>
                    <ViewerChart data={analyticsData?.viewer_timeline} />
                  </div>
                </div>
              )}
              
              {/* Stream Health Tab */}
              {activeTab === 'health' && (
                <div className="space-y-6" data-testid="health-tab-content">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-bold text-white">Stream Health</h3>
                    <Button size="sm" variant="outline" onClick={fetchStreamData} className="border-gray-700" data-testid="refresh-health">
                      <RefreshCw className="h-4 w-4 mr-2" />Refresh
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="health-stream-status">
                      <div className="flex items-center gap-2 mb-3">
                        <Signal className={`h-5 w-5 ${getHealthColor(streamHealth.quality)}`} />
                        <span className="text-gray-400">Stream status</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded-full ${cfStatus === 'connected' || streamStatus === 'live' ? 'bg-green-500' : 'bg-gray-500'}`} />
                        <span className={`font-medium ${cfStatus === 'connected' || streamStatus === 'live' ? 'text-green-400' : 'text-gray-400'}`}>
                          {streamHealth.status}
                        </span>
                      </div>
                      {event?.cloudflare_live_input_id && (
                        <p className="text-xs text-gray-500 mt-2">Live Input: {event.cloudflare_live_input_id.substring(0, 12)}...</p>
                      )}
                    </div>
                    
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="health-bitrate">
                      <div className="flex items-center gap-2 mb-3">
                        <Wifi className={`h-5 w-5 ${getHealthColor(streamHealth.quality)}`} />
                        <span className="text-gray-400">Bitrate</span>
                      </div>
                      <p className="text-xl font-bold text-white">
                        {streamHealth.bitrate > 0 ? `${streamHealth.bitrate} kbps` : 'No data'}
                      </p>
                      {streamHealth.bitrate > 0 && (
                        <div className="mt-2 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${streamHealth.bitrate > 4000 ? 'bg-green-500' : streamHealth.bitrate > 2000 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min((streamHealth.bitrate / 8000) * 100, 100)}%` }} />
                        </div>
                      )}
                    </div>
                    
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="health-resolution">
                      <div className="flex items-center gap-2 mb-3">
                        <Maximize className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-400">Resolution</span>
                      </div>
                      <p className="text-xl font-bold text-white">
                        {streamHealth.resolution !== '0x0' ? streamHealth.resolution : 'No data'}
                      </p>
                      {streamHealth.resolution !== '0x0' && (
                        <p className="text-xs text-gray-500 mt-1">
                          {streamHealth.resolution === '1920x1080' ? 'Full HD' : 
                           streamHealth.resolution === '1280x720' ? 'HD' :
                           streamHealth.resolution === '3840x2160' ? '4K UHD' : 'Custom'}
                        </p>
                      )}
                    </div>
                    
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800" data-testid="health-fps">
                      <div className="flex items-center gap-2 mb-3">
                        <Play className="h-5 w-5 text-gray-400" />
                        <span className="text-gray-400">Frame Rate</span>
                      </div>
                      <p className="text-xl font-bold text-white">
                        {streamHealth.fps > 0 ? `${streamHealth.fps} fps` : 'No data'}
                      </p>
                      {streamHealth.fps > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {streamHealth.fps >= 60 ? 'Smooth' : streamHealth.fps >= 30 ? 'Standard' : 'Low'}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Health Logs */}
                  <div className="bg-gray-900 rounded-xl border border-gray-800" data-testid="health-logs">
                    <div className="p-4 border-b border-gray-800 flex items-center justify-between">
                      <h4 className="font-medium text-white">Messages</h4>
                      <span className="text-xs text-gray-500">{healthLogs.length} entries</span>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {healthLogs.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                          <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="font-medium">Waiting for data</p>
                          <p className="text-sm mt-1">Stream health logs will appear when data is received</p>
                        </div>
                      ) : (
                        healthLogs.map((log) => (
                          <div key={log.id} className="px-4 py-3 border-b border-gray-800 last:border-0">
                            <div className="flex items-start gap-3">
                              <span className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">
                                {new Date(log.timestamp).toLocaleTimeString()}
                              </span>
                              <div>
                                <p className={`font-medium text-sm ${
                                  log.type === 'error' ? 'text-red-400' : 
                                  log.type === 'warning' ? 'text-yellow-400' : 'text-green-400'
                                }`}>
                                  {log.type === 'error' ? 'Error' : log.type === 'warning' ? 'Warning' : 'Info'}
                                </p>
                                <p className="text-sm text-gray-400">{log.message}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Chat Sidebar */}
        <div className="w-96 border-l border-gray-800 flex flex-col bg-gray-900/50">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium text-white">Live Chat</span>
              <Badge className="bg-gray-700 text-gray-300 text-xs">{chatMessages.length}</Badge>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {chatMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <MessageSquare className="h-12 w-12 mb-4 opacity-50" />
                <p className="font-medium">No messages yet</p>
                <p className="text-sm text-center mt-2">Chat messages will appear here when viewers start chatting</p>
              </div>
            ) : (
              chatMessages.map((msg) => (
                <div key={msg.id} className={`group flex items-start gap-2 ${msg.isSystem ? 'text-gray-500' : ''}`}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium text-sm ${
                        msg.isAdmin ? 'text-purple-400' : msg.isSystem ? 'text-gray-500' : 'text-blue-400'
                      }`}>
                        {msg.user}
                        {msg.isAdmin && <Badge className="ml-1 bg-purple-600 text-[10px] px-1 py-0">MOD</Badge>}
                      </span>
                      <span className="text-xs text-gray-600">
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-sm text-gray-300">{msg.message}</p>
                  </div>
                  {!msg.isSystem && (
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                      <button onClick={() => deleteMessage(msg.id)} className="p-1 text-gray-500 hover:text-red-400" title="Delete message">
                        <Trash2 className="h-3 w-3" />
                      </button>
                      <button className="p-1 text-gray-500 hover:text-yellow-400" title="Timeout user">
                        <Ban className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>
          
          <div className="p-4 border-t border-gray-800 bg-gray-900/50">
            <div className="bg-gray-800/50 rounded-lg p-3 mb-3">
              <div className="flex items-start gap-2">
                <Shield className="h-5 w-5 text-purple-400 mt-0.5" />
                <div>
                  <p className="font-medium text-white text-sm">Moderation active</p>
                  <p className="text-xs text-gray-400 mt-1">You can delete messages and timeout users</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Chat as Admin..."
                className="bg-gray-800 border-gray-700 text-white"
                data-testid="admin-chat-input"
              />
              <Button onClick={sendChatMessage} size="sm" className="bg-purple-600 hover:bg-purple-500" data-testid="send-chat-btn">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StreamControlPanel;
